import { useCallback, useEffect, useMemo, useState } from 'react'

import { CUSTOM_ATTRIBUTE_FORM_FIELD_PREFIX } from '@/constants'
import { getStereotypes } from '@/endpoints'
import { useApiRequest } from '@/endpoints/hooks'
import { StereotypeDto, StructureObjectDto } from '@/endpoints/models'
import { TableMode } from '@/enums'
import {
	useGetAllCustomAttributesForComboUsingGetQuery,
	useGetAllCustomAttributesUsingGetQuery,
} from '@/rtkApi'
import type {
	CustomAttributeComboDto,
	CustomAttributeStructureTypeEnum,
} from '@/rtkApi/mddApiSliceGenerated'
import { customAttributesUpsert } from '@/utils/collections'
import { logError } from '@/utils/errorHandling'

import {
	getAttributeStereotypesForStructureType,
	isCustomAttributeVisibleForStereotypes,
} from './customAttributesStereotypeFilter'

/** Ensures ARRAY-type custom attribute value is always an array (e.g. after LOV→ARRAY change). */
function ensureArrayTypeValue(val: unknown): unknown[] {
	if (Array.isArray(val)) return val
	if (val === undefined || val === null) return []
	return [{ value: val }]
}

/** value: any — custom attribute value can be string, number, boolean, or array depending on type */
export type CustomAttributeForm = {
	id: number | undefined
	name: string
	type?: string
	value: any
	version: number | undefined
}

/** Form values map; index signature any for dynamic form fields per structure type */
export type FormType = {
	[index: string]: any
	customAttributes?: CustomAttributeForm[]
}

export const useCustomAttributesInitValues = (
	form: FormType | undefined,
	structureType: StructureObjectDto.TypeEnum,
	mode?: TableMode,
	batchStereotypeIds?: number[],
) => {
	const [structureTypeStereotypes, setStructureTypeStereotypes] = useState<
		StereotypeDto[]
	>([])

	const { data: customAttributes = [] } =
		useGetAllCustomAttributesForComboUsingGetQuery({
			structureType: structureType as CustomAttributeStructureTypeEnum,
		})

	const { data: customAttributesAllData } = useGetAllCustomAttributesUsingGetQuery()
	const request = useApiRequest()

	useEffect(() => {
		let stereotypeType: string | undefined

		switch (structureType) {
			case StructureObjectDto.TypeEnum.MAPPING_DEFAULT:
				stereotypeType = StereotypeDto.TypeEnum.MAPPING
				break
			case StructureObjectDto.TypeEnum.SYSTEM:
				setStructureTypeStereotypes([])
				return
			case StructureObjectDto.TypeEnum.API_COLLECTION:
				stereotypeType = StereotypeDto.TypeEnum.API_ROWSET
				break
			default:
				stereotypeType = structureType as string
		}

		request(getStereotypes({ type: stereotypeType })).then((res) => {
			setStructureTypeStereotypes(res?.data ?? [])
		})
	}, [request, structureType])

	const getCustomAttributesFiltered = useCallback(
		(stereotypeId?: number) => {
			if (
				[(TableMode.HISTORY, TableMode.REFERENCE)].includes(
					mode ?? TableMode.TABLE,
				)
			) {
				return [] as CustomAttributeComboDto[]
			}

			return (
				customAttributes.filter((customAttribute) => {
					const stereotypesForType = getAttributeStereotypesForStructureType(
						customAttribute.stereotypeIds,
						structureTypeStereotypes.map((stereotype) => stereotype.id),
					)

					return isCustomAttributeVisibleForStereotypes(
						stereotypesForType,
						stereotypeId,
						batchStereotypeIds,
					)
				}) ?? []
			)
		},
		[batchStereotypeIds, customAttributes, mode, structureTypeStereotypes],
	)

	const customAttributesFiltered = useMemo(
		() => getCustomAttributesFiltered(form?.stereotypeId),
		[form, getCustomAttributesFiltered],
	)

	const customAttributesAll = useMemo(
		() => ({ data: customAttributesAllData }),
		[customAttributesAllData],
	)

	const initialValuesCustomAttributes = useMemo(
		() =>
			form?.customAttributes?.reduce(
				(initialValues, customAttribute) => {
					const fieldName = `${CUSTOM_ATTRIBUTE_FORM_FIELD_PREFIX}.${customAttribute.id}.${customAttribute.name}`
					const attrCombo = customAttributesAll.data?.find(
						(a) => a.id === customAttribute.id,
					)
					const value =
						attrCombo?.type === 'ARRAY'
							? ensureArrayTypeValue(customAttribute.value)
							: customAttribute.value

					return {
						...initialValues,
						[fieldName]: value,
					}
				},
				{} as Record<string, any>,
			) ?? {},
		[form, customAttributesAll.data],
	)

	const resetValuesCustomAttributes = useMemo(
		() =>
			Object.keys(initialValuesCustomAttributes).reduce(
				(reset, key) => ({
					...reset,
					[key]: undefined,
				}),
				{},
			),
		[initialValuesCustomAttributes],
	)

	const getCustomAttributesReset = useCallback(
		(newStereotypeId?: number) => {
			const newCustomAttributes = getCustomAttributesFiltered(newStereotypeId)

			const customAttributesRedux = form?.customAttributes?.filter(
				(customAttribute) =>
					newCustomAttributes.some(
						(newCustomAttribute) =>
							newCustomAttribute.id === customAttribute.id,
					),
			)

			const customAttributesForm =
				customAttributesRedux?.reduce(
					(formValues, customAttribute) => {
						const combo = newCustomAttributes.find(
							(a) => a.id === customAttribute.id,
						)
						const value =
							combo?.type === 'ARRAY'
								? ensureArrayTypeValue(customAttribute.value)
								: customAttribute.value
						return {
							...formValues,
							[`${CUSTOM_ATTRIBUTE_FORM_FIELD_PREFIX}.${customAttribute.id}.${customAttribute.name}`]:
								value,
						}
					},
					resetValuesCustomAttributes,
				) ?? resetValuesCustomAttributes

			return {
				customAttributesRedux,
				customAttributesForm,
			}
		},
		[form, getCustomAttributesFiltered, resetValuesCustomAttributes],
	)

	/** Check if update contains custom attribute and format the update object accordingly */
	const parseCustomAttribute = useCallback(
		(
			update: Record<string, any>,
			customAttributes?: CustomAttributeForm[],
			customAttrTemplates?: CustomAttributeComboDto[],
		) => {
			return Object.keys(update).reduce(
				(updateNew, key) => {
					if (key.includes(CUSTOM_ATTRIBUTE_FORM_FIELD_PREFIX)) {
						const [_, id] = key.split('.')
						const customAttributeId = parseFloat(id)

						const attribute = customAttributesAll.data?.find(
							(customAttribute) => customAttribute.id === customAttributeId,
						)

						const getParsedAndSyncedCustomAttributes = () => {
							const parsedCA = customAttributes?.reduce(
								(acc: CustomAttributeForm[], { id, name, version, value }) => {
									// Convert id and version to numbers if they are strings
									id = !isNaN(id) ? parseInt(id.toString()) : id

									version = !isNaN(version)
										? parseInt(version.toString())
										: version

									return [...acc, { id, name, version, value }]
								},
								[],
							)

							const syncCustomAttributesWithTemplates = parsedCA?.filter(
								(customAttribute) =>
									customAttrTemplates?.find(
										(customAttrTemplate: CustomAttributeComboDto) => {
											return customAttrTemplate.id === customAttribute.id
										},
									),
							)

							return syncCustomAttributesWithTemplates
						}

						if (attribute === undefined) {
							logError(new Error('Unknown attribute'), { operation: 'useCustomAttributes.syncCustomAttributesWithTemplates' })

							return updateNew
						}

						const updatedCustomAttribute: CustomAttributeForm = {
							id: attribute.id as number,
							name: attribute.name,
							value: update[key],
							version: attribute.version as number,
						}

						return {
							...updateNew,
							customAttributes: customAttributesUpsert(
								getParsedAndSyncedCustomAttributes() ?? [],
								updatedCustomAttribute,
							),
						}
					}

					return {
						...updateNew,
						[key]: update[key],
					}
				},
				{} as Record<string, any>,
			)
		},
		[customAttributesAll.data],
	)

	return useMemo(
		() => ({
			initialValuesCustomAttributes,
			parseCustomAttribute,
			customAttributes: customAttributesFiltered,
			getCustomAttributesReset,
			customAttributesAll,
		}),
		[
			initialValuesCustomAttributes,
			parseCustomAttribute,
			customAttributesFiltered,
			getCustomAttributesReset,
			customAttributesAll,
		],
	)
}
